import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Settings, Save, Clock, Coffee, Calendar, DollarSign, Moon, Sun } from 'lucide-react';
import { toast } from 'sonner';

export default function HRSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    work_start_time: '08:30',
    work_end_time: '17:30',
    standard_work_hours: 8,
    grace_period_minutes: 15,
    break_enabled: true,
    break_slot_1_start: '12:30',
    break_slot_1_end: '13:30',
    break_slot_1_positions: ['Agent'],
    break_slot_2_start: '13:30',
    break_slot_2_end: '14:30',
    break_slot_2_positions: ['Chef de service', 'Manager', 'Directeur'],
    break_duration_minutes: 60,
    break_penalty_type: 'hourly_rate',
    saturday_enabled: true,
    saturday_start_time: '08:00',
    saturday_end_time: '12:00',
    saturday_work_hours: 4,
    night_shift_start_time: '19:00',
    night_shift_end_time: '05:00',
    night_shift_break_enabled: false,
    night_shift_break_duration_minutes: 60,
    night_shift_work_hours: 10,
    late_penalty_per_hour: 0,
    absence_penalty_per_day: 0,
    break_overtime_penalty_per_hour: 0,
    overtime_rate: 1.5,
    overtime_rate_day: 1.3,
    overtime_rate_night: 1.6,
    overtime_rate_holiday: 2.0,
    paid_leave_days: 18,
    paid_leave_days_cadre: 21,
    absence_deduction_type: 'daily_rate',
    max_late_per_month: 0
  });

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/hr-settings');
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      toast.error('Erreur lors du chargement des parametres');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put('/hr-settings', settings);
      toast.success('Parametres mis a jour avec succes');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => setSettings(prev => ({ ...prev, [field]: value }));

  if (loading) return <div className="text-center py-8">Chargement...</div>;

  return (
    <div className="space-y-6" data-testid="hr-settings-page">
      <div>
        <h1 className="font-heading font-bold text-3xl text-slate-900">Parametres RH</h1>
        <p className="text-slate-600 mt-1">Configuration des politiques de l'entreprise</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">

        {/* Weekday Schedule (Mon-Fri) */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sun className="w-5 h-5 text-amber-500" />
              Horaire Semaine (Lundi - Vendredi)
            </CardTitle>
            <CardDescription>
              Plage horaire de jour pour les employes en shift normal
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Heure de debut</Label>
                <Input type="time" value={settings.work_start_time} onChange={(e) => updateField('work_start_time', e.target.value)} data-testid="work-start-input" />
              </div>
              <div className="space-y-2">
                <Label>Heure de fin</Label>
                <Input type="time" value={settings.work_end_time} onChange={(e) => updateField('work_end_time', e.target.value)} data-testid="work-end-input" />
              </div>
              <div className="space-y-2">
                <Label>Heures effectives / jour</Label>
                <Input type="number" step="0.5" value={settings.standard_work_hours} onChange={(e) => updateField('standard_work_hours', parseFloat(e.target.value))} data-testid="standard-hours-input" />
                <p className="text-xs text-slate-500">Pause deduite de la plage horaire</p>
              </div>
            </div>

            {/* Break toggle */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div>
                <p className="font-medium text-slate-800">Pause dejeuner</p>
                <p className="text-sm text-slate-500">Activer la pause de 1h sur l'horaire semaine</p>
              </div>
              <Switch
                checked={settings.break_enabled}
                onCheckedChange={(val) => updateField('break_enabled', val)}
                data-testid="break-enabled-switch"
              />
            </div>

            {settings.break_enabled && (
              <div className="space-y-4 pl-4 border-l-2 border-amber-300">
                {/* Slot 1 - Agent */}
                <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                  <p className="font-medium text-amber-800 mb-3">Creneau 1 - Poste : Agent</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Debut de pause</Label>
                      <Input type="time" value={settings.break_slot_1_start} onChange={(e) => updateField('break_slot_1_start', e.target.value)} data-testid="break-slot1-start" />
                    </div>
                    <div className="space-y-2">
                      <Label>Fin de pause</Label>
                      <Input type="time" value={settings.break_slot_1_end} onChange={(e) => updateField('break_slot_1_end', e.target.value)} data-testid="break-slot1-end" />
                    </div>
                  </div>
                  <p className="text-xs text-amber-600 mt-2">Postes concernes : {(settings.break_slot_1_positions || []).join(', ')}</p>
                </div>

                {/* Slot 2 - Cadres */}
                <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
                  <p className="font-medium text-indigo-800 mb-3">Creneau 2 - Postes : Chef de service, Manager, Directeur</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Debut de pause</Label>
                      <Input type="time" value={settings.break_slot_2_start} onChange={(e) => updateField('break_slot_2_start', e.target.value)} data-testid="break-slot2-start" />
                    </div>
                    <div className="space-y-2">
                      <Label>Fin de pause</Label>
                      <Input type="time" value={settings.break_slot_2_end} onChange={(e) => updateField('break_slot_2_end', e.target.value)} data-testid="break-slot2-end" />
                    </div>
                  </div>
                  <p className="text-xs text-indigo-600 mt-2">Postes concernes : {(settings.break_slot_2_positions || []).join(', ')}</p>
                </div>

                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-800">
                    <strong>Regle :</strong> Meme si l'employe commence sa pause en retard, il doit terminer a l'heure prevue. 
                    Le depassement de l'heure de fin est penalise proportionnellement au taux horaire.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Saturday Schedule */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-500" />
              Horaire Samedi
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div>
                <p className="font-medium text-slate-800">Samedi travaille</p>
                <p className="text-sm text-slate-500">Activer le travail le samedi pour l'entreprise</p>
              </div>
              <Switch
                checked={settings.saturday_enabled}
                onCheckedChange={(val) => updateField('saturday_enabled', val)}
                data-testid="saturday-enabled-switch"
              />
            </div>

            {settings.saturday_enabled && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pl-4 border-l-2 border-blue-300">
                <div className="space-y-2">
                  <Label>Heure de debut</Label>
                  <Input type="time" value={settings.saturday_start_time} onChange={(e) => updateField('saturday_start_time', e.target.value)} data-testid="saturday-start-input" />
                </div>
                <div className="space-y-2">
                  <Label>Heure de fin</Label>
                  <Input type="time" value={settings.saturday_end_time} onChange={(e) => updateField('saturday_end_time', e.target.value)} data-testid="saturday-end-input" />
                </div>
                <div className="space-y-2">
                  <Label>Heures effectives</Label>
                  <Input type="number" step="0.5" value={settings.saturday_work_hours} onChange={(e) => updateField('saturday_work_hours', parseFloat(e.target.value))} data-testid="saturday-hours-input" />
                </div>
              </div>
            )}

            {settings.saturday_enabled && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">Pas de pause le samedi. Les heures effectives sont directes.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Night Shift Schedule */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Moon className="w-5 h-5 text-indigo-500" />
              Horaire de Nuit
            </CardTitle>
            <CardDescription>
              Plage horaire pour les employes en shift de nuit (19h00 - 05h00)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Heure de debut</Label>
                <Input type="time" value={settings.night_shift_start_time} onChange={(e) => updateField('night_shift_start_time', e.target.value)} data-testid="night-start-input" />
              </div>
              <div className="space-y-2">
                <Label>Heure de fin</Label>
                <Input type="time" value={settings.night_shift_end_time} onChange={(e) => updateField('night_shift_end_time', e.target.value)} data-testid="night-end-input" />
              </div>
              <div className="space-y-2">
                <Label>Heures effectives / nuit</Label>
                <Input type="number" step="0.5" value={settings.night_shift_work_hours} onChange={(e) => updateField('night_shift_work_hours', parseFloat(e.target.value))} data-testid="night-hours-input" />
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div>
                <p className="font-medium text-slate-800">Pause de nuit</p>
                <p className="text-sm text-slate-500">Activer une pause pendant le shift de nuit</p>
              </div>
              <Switch
                checked={settings.night_shift_break_enabled}
                onCheckedChange={(val) => updateField('night_shift_break_enabled', val)}
                data-testid="night-break-switch"
              />
            </div>

            {settings.night_shift_break_enabled && (
              <div className="pl-4 border-l-2 border-indigo-300">
                <div className="space-y-2 max-w-xs">
                  <Label>Duree de la pause nuit (minutes)</Label>
                  <Input type="number" value={settings.night_shift_break_duration_minutes} onChange={(e) => updateField('night_shift_break_duration_minutes', parseInt(e.target.value))} data-testid="night-break-duration-input" />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Lateness & Penalties */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-red-500" />
              Retards et Penalites
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Periode de grace (minutes)</Label>
                <Input type="number" value={settings.grace_period_minutes} onChange={(e) => updateField('grace_period_minutes', parseInt(e.target.value))} data-testid="grace-period-input" />
                <p className="text-xs text-slate-500">Retard tolere sans penalite</p>
              </div>
              <div className="space-y-2">
                <Label>Penalite par heure de retard</Label>
                <Input type="number" step="0.01" value={settings.late_penalty_per_hour} onChange={(e) => updateField('late_penalty_per_hour', parseFloat(e.target.value))} data-testid="late-penalty-input" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Penalite depassement de pause (/heure)</Label>
                <Input type="number" step="0.01" value={settings.break_overtime_penalty_per_hour} onChange={(e) => updateField('break_overtime_penalty_per_hour', parseFloat(e.target.value))} data-testid="break-overtime-penalty-input" />
              </div>
              <div className="space-y-2">
                <Label>Penalite par jour d&apos;absence</Label>
                <Input type="number" step="0.01" value={settings.absence_penalty_per_day} onChange={(e) => updateField('absence_penalty_per_day', parseFloat(e.target.value))} data-testid="absence-penalty-input" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Seuil d&apos;alerte retards / mois</Label>
                <Input
                  type="number"
                  min="0"
                  value={settings.max_late_per_month ?? 0}
                  onChange={(e) => updateField('max_late_per_month', parseInt(e.target.value) || 0)}
                  data-testid="max-late-per-month-input"
                />
                <p className="text-xs text-slate-500">
                  Au-dela de ce nombre de retards dans le mois, un email d&apos;alerte est envoye a l&apos;employe et au RH (0 = desactive).
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Leave & Overtime */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-green-500" />
              Conges Annuels par Categorie
            </CardTitle>
            <CardDescription>
              Le nombre de jours depend de la categorie de l'employe
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <p className="font-medium text-slate-700 mb-2">Manoeuvre, Agents de maitrise, Cadre de collaboration</p>
                <div className="space-y-2">
                  <Label>Jours de conge annuel</Label>
                  <Input type="number" value={settings.paid_leave_days || 18} onChange={(e) => updateField('paid_leave_days', parseInt(e.target.value))} data-testid="paid-leave-standard-input" />
                </div>
              </div>
              <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
                <p className="font-medium text-indigo-700 mb-2">Cadre de direction, Cadre dirigeant</p>
                <div className="space-y-2">
                  <Label>Jours de conge annuel</Label>
                  <Input type="number" value={settings.paid_leave_days_cadre || 21} onChange={(e) => updateField('paid_leave_days_cadre', parseInt(e.target.value))} data-testid="paid-leave-cadre-input" />
                </div>
              </div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-800">
                <strong>Absence :</strong> Toute absence non justifiee entraine la retenue du taux journalier complet (taux horaire x heures/jour).
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Overtime */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-500" />
              Heures Supplementaires - Taux
            </CardTitle>
            <CardDescription>Majoration par rapport au taux horaire de base</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                <div className="flex items-center gap-2 mb-2">
                  <Sun className="w-4 h-4 text-amber-500" />
                  <Label className="text-amber-800 font-medium">HS Jour (+30%)</Label>
                </div>
                <Input type="number" step="0.1" value={settings.overtime_rate_day || 1.3} onChange={(e) => updateField('overtime_rate_day', parseFloat(e.target.value))} data-testid="ot-rate-day-input" />
                <p className="text-xs text-amber-600 mt-1">Multiplicateur: x{settings.overtime_rate_day || 1.3}</p>
              </div>
              <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
                <div className="flex items-center gap-2 mb-2">
                  <Moon className="w-4 h-4 text-indigo-500" />
                  <Label className="text-indigo-800 font-medium">HS Nuit (+60%)</Label>
                </div>
                <Input type="number" step="0.1" value={settings.overtime_rate_night || 1.6} onChange={(e) => updateField('overtime_rate_night', parseFloat(e.target.value))} data-testid="ot-rate-night-input" />
                <p className="text-xs text-indigo-600 mt-1">Multiplicateur: x{settings.overtime_rate_night || 1.6}</p>
              </div>
              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="w-4 h-4 text-red-500" />
                  <Label className="text-red-800 font-medium">HS Dimanche/Ferie (+100%)</Label>
                </div>
                <Input type="number" step="0.1" value={settings.overtime_rate_holiday || 2.0} onChange={(e) => updateField('overtime_rate_holiday', parseFloat(e.target.value))} data-testid="ot-rate-holiday-input" />
                <p className="text-xs text-red-600 mt-1">Multiplicateur: x{settings.overtime_rate_holiday || 2.0}</p>
              </div>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
              <p className="text-sm text-slate-700">Les HS doivent etre validees par : <strong>Chef de direction → RH → Directeur</strong> avant prise en compte sur la paie.</p>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button type="submit" disabled={saving} className="min-w-[150px]" data-testid="save-settings-btn">
            {saving ? 'Enregistrement...' : (<><Save className="w-4 h-4 mr-2" />Enregistrer</>)}
          </Button>
        </div>
      </form>
    </div>
  );
}
